import { Btn, ToolTip } from "AbstractElements";
import { Cancel } from "Constant";
import { useMenu } from "Layout/Sidebar/useMenu";
import { useEffect, useMemo, useRef, useState } from "react";
import { TableColumn } from "react-data-table-component";
import { Card, CardBody, CardFooter, CardHeader, Modal } from "reactstrap";
import { useCurrentResource } from "./useCurrentResource";
import { ResourceType } from "routers/constant";

interface IProps<T> {
  columns: TableColumn<T>[];
}

export const useTableColumns = <T,>(props: IProps<T>) => {
  const { columns } = props;
  const results = useMemo(
    () =>
      columns.map((column, i) => {
        const { cell, selector } = column;
        if (!cell) {
          column.cell = (row) => (
            <TableCellText index={i} text={selector?.(row)} />
          );
        }
        return { sortable: true, ...column };
      }),
    [columns]
  );
  return {
    columns: results,
  };
};

interface IUseActionsCell {
  onEdit?: (id: string) => void;
  onPrint?: (id: string) => void;
  onView?: (id: string, params?: any) => void;
  onDelete?: (id: string) => void;
  ExtraAction?: any;
  deleteTitle?: string;
}
export const useActionsCell = (props: IUseActionsCell) => {
  const {
    deleteTitle = "Xóa dữ liệu này?",
    onEdit,
    onPrint,
    onView,
    onDelete,
    ExtraAction,
  } = props;
  const [open, setOpen] = useState(false);
  const resourceName = useCurrentResource();
  const { can } = useMenu();

  const resourceCan = can[resourceName] || {};

  const hasActions =
    resourceCan[ResourceType.EDIT] ||
    resourceCan[ResourceType.DETAIL] ||
    resourceCan[ResourceType.DELETE];

  const actionColumns: TableColumn<any>[] = !hasActions
    ? []
    : [
        {
          name: "Hành động",
          button: true,
          cell: (row) => (
            <div className="flex gap-2">
              {onEdit && resourceCan[ResourceType.EDIT] && (
                <Btn
                  attrBtn={{
                    className: "px-2 py-1",
                    color: "info",
                    onClick: () => onEdit(row.id),
                  }}
                >
                  <i className="fa fa-edit"></i>
                </Btn>
              )}
              {onView && resourceCan[ResourceType.DETAIL] && (
                <Btn
                  attrBtn={{
                    className: "px-2 py-1",
                    color: "info",
                    onClick: () => onView(row.id, row),
                  }}
                >
                  <i className="fa fa-eye"></i>
                </Btn>
              )}
              {onPrint && (
                <Btn
                  attrBtn={{
                    className: "px-2 py-1",
                    color: "secondary",
                    onClick: () => onPrint(row.id),
                  }}
                >
                  <i className="fa fa-print"></i>
                </Btn>
              )}
              {onDelete && resourceCan[ResourceType.DELETE] && (
                <>
                  <Btn
                    attrBtn={{
                      className: "px-2 py-1",
                      color: "secondary",
                      onClick: () => setOpen(true),
                    }}
                  >
                    <i className="fa fa-trash"></i>
                  </Btn>
                  <Modal isOpen={open}>
                    <Card className="mb-0">
                      <CardHeader className="font-bold text-lg">
                        Đồng ý xóa?
                      </CardHeader>
                      <CardBody>{deleteTitle}</CardBody>
                      <CardFooter className="flex justify-end">
                        <Btn
                          attrBtn={{
                            color: "danger",
                            className: "me-3",
                            onClick: () => setOpen(false),
                          }}
                        >
                          {Cancel}
                        </Btn>
                        <Btn
                          attrBtn={{
                            color: "success",
                            className: "me-3",
                            onClick: () => {
                              onDelete(row.id);
                              setOpen(false);
                            },
                          }}
                        >
                          Có
                        </Btn>
                      </CardFooter>
                    </Card>
                  </Modal>
                </>
              )}
              {ExtraAction ? <ExtraAction row={row} /> : <></>}
            </div>
          ),
          minWidth: "160px",
          maxWidth: "160px",
        },
      ];
  return {
    actionColumns,
  };
};

export const TableCellText = ({ text, index }) => {
  const [basictooltip, setbasictooltip] = useState(false);
  const toggle = () => setbasictooltip(!basictooltip);
  const target = useRef<HTMLElement>(null);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      const result =
        target.current?.offsetWidth !== target.current?.scrollWidth;
      setTruncated(result);
    }, 1000);
  }, [target]);

  return (
    <>
      <span ref={target} className="truncate" id={`text-${index}`}>
        {text}
      </span>
      {truncated && (
        <ToolTip
          attrToolTip={{
            placement: "top",
            isOpen: basictooltip,
            target: `text-${index}`,
            toggle: toggle,
          }}
        >
          {text}
        </ToolTip>
      )}
    </>
  );
};
